import {
  feedbackScreenshotProblem,
  type FeedbackCategory,
  type FeedbackRequest,
} from "@msc/shared";

/**
 * Die reine Logik hinter dem Feedback-Dialog — bewusst getrennt von der
 * Komponente, damit sie ohne Browser prüfbar ist.
 */

/** Grenze aus `feedbackRequestSchema.page`. Hier gekappt statt drüben abgelehnt. */
const SEITE_MAX = 500;

/**
 * Wo der Melder stand: Pfad samt Abfrage, ohne Host.
 *
 * Der Host bliebe ohne Aussage (die Umgebung steht ohnehin im Kontext) und
 * trüge in einer Entwicklungsumgebung eine Portnummer, die niemanden angeht.
 * Der **Hash** bleibt ebenfalls weg — er erreicht den Server bei keiner
 * Anfrage und wäre die einzige Stelle, an der ein Bezeichner aus der Seite
 * unbeabsichtigt mitwanderte.
 */
export function feedbackSeitenPfad(ort: { pathname: string; search: string }): string {
  const pfad = `${ort.pathname}${ort.search}`.trim() || "/";
  return pfad.length > SEITE_MAX ? pfad.slice(0, SEITE_MAX) : pfad;
}

/**
 * Baut den Rumpf für `POST /feedback`.
 *
 * `appVersion` fällt auf `"unbekannt"` zurück: Die Version kommt aus
 * `GET /health/live` und ist bei einem sehr frühen Klick noch nicht geladen.
 * Ein leerer Wert würde vom Server mit 400 abgewiesen — und der Melder stünde
 * vor einer Fehlermeldung über ein Feld, das er nie gesehen hat.
 */
export function feedbackRumpf(eingabe: {
  category: FeedbackCategory;
  title: string;
  text: string;
  ort: { pathname: string; search: string };
  appVersion: string | null;
}): FeedbackRequest {
  return {
    category: eingabe.category,
    title: eingabe.title.trim(),
    text: eingabe.text.trim(),
    page: feedbackSeitenPfad(eingabe.ort),
    appVersion: eingabe.appVersion?.trim() || "unbekannt",
  };
}

/**
 * Das Ergebnis eines Einfügens aus der Zwischenablage: ein brauchbares Bild,
 * ein benennbares Problem, oder gar nichts.
 *
 * `null` heißt ausdrücklich **nicht** „Fehler", sondern „das war kein Bild" —
 * der übliche Fall, wenn jemand Text in das Textfeld einfügt. Diese
 * Unterscheidung ist der Grund für den dreiwertigen Rückgabewert: Ein
 * `File | null` müsste jeden Textabsatz als misslungenen Screenshot melden.
 */
export type ZwischenablageErgebnis =
  | { datei: File }
  | { problem: "type" | "size" | "empty" }
  | null;

/**
 * Prüft die Dateien eines Einfüge-Ereignisses und liefert den ersten
 * Screenshot — mit derselben Regel wie die Dateiauswahl und die API
 * (`feedbackScreenshotProblem` aus `@msc/shared`).
 *
 * Beim Einfügen eines Bildschirmfotos liegt in `clipboardData.files` genau
 * eine Datei, meist `image.png` ohne sprechenden Namen. Enthält die
 * Zwischenablage mehrere, zählt die erste: Ein Dialog mit einem Bildfeld kann
 * nicht zwei annehmen, und stillschweigend die letzte zu nehmen wäre
 * willkürlich.
 */
export function screenshotAusZwischenablage(dateien: readonly File[]): ZwischenablageErgebnis {
  const erste = dateien[0];
  if (!erste) return null;
  const problem = feedbackScreenshotProblem({ type: erste.type, size: erste.size });
  return problem === null ? { datei: erste } : { problem };
}
